'use client';

import type { LocaleCode } from '@ifixit/i18n/types';
import { setLanguagePreference, setLocalePreference } from '@ifixit/i18n/locale-preference';
import { fromPhpLangId, type PhpLangid } from '@ifixit/i18n/php-langid';
import {
   type Country,
   type UserLocaleCode,
   countryByCode,
   defaultUserLocaleByRouteLocale,
   parseUserLocale,
   routeLocaleByUserLocale,
} from '@ifixit/i18n/user-locale';
import { getCookieDomain } from '@ifixit/helpers';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { SentryError } from '@ifixit/sentry';
import { sendReactNativeMessage } from '@ifixit/utils';
import {
   createContext,
   type MouseEvent,
   type RefObject,
   use,
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
   type ReactNode,
} from 'react';
import { useCookieState, readCookie, notifyCookieChange } from '../hooks/use-cookie-state';
import { useDetectedUserLocale } from '../hooks/use-detected-user-locale';
import { useBrowserLanguage } from '../hooks/use-browser-language';
import {
   parseRawCookieValue,
   resolveUserLocaleForPhpLangId,
   resolveUserLocaleForRouteLocale,
   resolveUserLocaleFromPreference,
} from '@ifixit/i18n/resolve-user-locale';

export interface SuggestedLocale {
   userLocale: UserLocaleCode;
   routeLocale: LocaleCode;
   country: Country;
}

const identityRouteLocale = (code: LocaleCode): LocaleCode => code;

interface LocationContextValue {
   isPickerOpen: boolean;
   openPicker: () => void;
   closePicker: () => void;
   selectionMadeRef: RefObject<boolean>;
   localeSuggestionMessages: LocaleSuggestionMessages | null;
   userLocale: UserLocaleCode;
   setUserLocale: (locale: UserLocaleCode) => Promise<unknown>;
   suggestedLocale: SuggestedLocale | null;
   currentRouteLocale: LocaleCode | null;
   currentPhpLangId: string | null;
   hrefFromUserLocale: (userLocale: UserLocaleCode) => string | null;
   demoteRouteLocale: (code: LocaleCode) => LocaleCode;
   isCarpenter: boolean;
   withProgressBar: (callback: () => Promise<void>) => Promise<void>;
   applySelectionInPlace: (() => void) | null;
}

export interface LocaleSuggestionMessages {
   locale: string;
   messages: Record<string, unknown>;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export interface LocationProviderProps {
   currentRouteLocale?: LocaleCode;
   currentPhpLangId?: PhpLangid;
   /** PHP langid stored on the account, for logged-in users. */
   accountLangid?: string | null;
   geoDetectedUserLocale?: UserLocaleCode | null;
   geoPending?: boolean;
   hrefFromUserLocale: (userLocale: UserLocaleCode) => string | null;
   demoteRouteLocale?: (code: LocaleCode) => LocaleCode;
   isCarpenter: boolean;
   withProgressBar: (callback: () => Promise<void>) => Promise<void>;
   /**
    * When set, picking a locale saves the preference without navigating, then
    * calls this to refresh the current page (e.g. Next.js router.refresh()).
    */
   applySelectionInPlace?: (() => void) | null;
   localeSuggestionMessages?: Record<string, Record<string, unknown>>;
   children: ReactNode;
}

export function LocationProvider({
   currentRouteLocale,
   currentPhpLangId,
   accountLangid = null,
   geoDetectedUserLocale,
   geoPending = false,
   hrefFromUserLocale,
   demoteRouteLocale = identityRouteLocale,
   isCarpenter,
   withProgressBar,
   applySelectionInPlace = null,
   localeSuggestionMessages = {},
   children,
}: LocationProviderProps) {
   const picker = usePickerState();
   const resolvedMessages = useSuggestionMessages(localeSuggestionMessages);
   const locale = useUserLocaleState({
      currentRouteLocale,
      currentPhpLangId,
      accountLangid,
      geoDetectedUserLocale,
      geoPending,
      demoteRouteLocale,
      // In-place surfaces have no locale in the URL, so the route locale must
      // not override the stored preference when resolving the active locale.
      resolveFromPreference: applySelectionInPlace != null,
   });
   const suggestedLocale = useSuggestedLocale(
      currentRouteLocale,
      locale.rawCookieValue,
      demoteRouteLocale
   );

   const value = useMemo(
      () => ({
         ...picker,
         localeSuggestionMessages: resolvedMessages,
         userLocale: locale.userLocale,
         setUserLocale: locale.setUserLocale,
         suggestedLocale,
         currentRouteLocale: currentRouteLocale ?? null,
         currentPhpLangId: currentPhpLangId ?? null,
         hrefFromUserLocale,
         demoteRouteLocale,
         isCarpenter,
         withProgressBar,
         applySelectionInPlace,
      }),
      [
         picker,
         resolvedMessages,
         locale.userLocale,
         locale.setUserLocale,
         suggestedLocale,
         currentRouteLocale,
         currentPhpLangId,
         hrefFromUserLocale,
         demoteRouteLocale,
         isCarpenter,
         withProgressBar,
         applySelectionInPlace,
      ]
   );

   return <LocationContext value={value}>{children}</LocationContext>;
}

// --- Public hooks (consumer API) ---

export function useLocationPicker() {
   const { isPickerOpen, openPicker, closePicker, selectionMadeRef } = useLocationContext();
   return { isPickerOpen, openPicker, closePicker, selectionMadeRef };
}

export function useLocaleSuggestionMessages(): LocaleSuggestionMessages | null {
   return useLocationContext().localeSuggestionMessages;
}

export function useSuggestedLocaleChange(): SuggestedLocale | null {
   return useLocationContext().suggestedLocale;
}

export function useUserLocale(): [
   UserLocaleCode,
   (userLocale: UserLocaleCode) => Promise<unknown>,
] {
   const { userLocale, setUserLocale } = useLocationContext();
   return [userLocale, setUserLocale];
}

export function useUserCountry(): Country {
   const { userLocale } = useLocationContext();
   return countryByCode[parseUserLocale(userLocale).country];
}

export function useLocationNavigation() {
   const {
      currentRouteLocale,
      currentPhpLangId,
      hrefFromUserLocale,
      demoteRouteLocale,
      withProgressBar,
      applySelectionInPlace,
   } = useLocationContext();
   return {
      currentRouteLocale,
      currentPhpLangId,
      hrefFromUserLocale,
      demoteRouteLocale,
      withProgressBar,
      applySelectionInPlace,
   };
}

export function useIsCarpenter() {
   return useLocationContext().isCarpenter;
}

/**
 * onClick for links that switch the user's store or language, like the ones in
 * the header's location picker.
 *
 * A plain click saves the choice, then does a full page load of the link instead
 * of Next.js's in-place navigation, because some things on a page can't change
 * store once loaded: the Trusted Shops badge's script only shows one shop per
 * page. It waits for the save first, since leaving the page cancels any request
 * still in flight.
 *
 * A cmd-, ctrl-, shift-, or alt-click saves the choice too, but leaves the link
 * to the browser, like any other link.
 */
export async function handleLocaleLinkClick(
   clickEvent: MouseEvent,
   saveLocale: () => Promise<unknown>
) {
   const url = clickEvent.currentTarget.getAttribute('href');
   if (url == null) return;
   const opensElsewhere =
      clickEvent.metaKey ||
      clickEvent.ctrlKey ||
      clickEvent.shiftKey ||
      clickEvent.altKey ||
      clickEvent.button !== 0;
   if (!opensElsewhere) clickEvent.preventDefault();
   await saveLocale();
   if (!opensElsewhere) window.location.assign(url);
}

// --- Private hooks (implementation details) ---

function useLocationContext() {
   const context = use(LocationContext);
   if (!context) {
      throw new SentryError('useLocationContext must be used within LocationProvider');
   }
   return context;
}

function usePickerState() {
   const [isPickerOpen, setIsPickerOpen] = useState(false);
   const selectionMadeRef = useRef(false);

   return useMemo(
      () => ({
         isPickerOpen,
         openPicker: () => {
            selectionMadeRef.current = false;
            setIsPickerOpen(true);
         },
         closePicker: () => setIsPickerOpen(false),
         selectionMadeRef,
      }),
      [isPickerOpen]
   );
}

function useSuggestionMessages(
   localeSuggestionMessages: Record<string, Record<string, unknown>>
): LocaleSuggestionMessages | null {
   const browserLocale = useBrowserLanguage() ?? 'en';

   return useMemo(() => {
      const messages = localeSuggestionMessages[browserLocale] ?? localeSuggestionMessages['en'];
      return messages ? { locale: fromPhpLangId(browserLocale), messages } : null;
   }, [localeSuggestionMessages, browserLocale]);
}

function useSuggestedLocale(
   currentRouteLocale: LocaleCode | undefined,
   rawCookieValue: string | null | undefined,
   demoteRouteLocale: (code: LocaleCode) => LocaleCode
): SuggestedLocale | null {
   const detectedUserLocale = useDetectedUserLocale();

   return useMemo(() => {
      if (!detectedUserLocale || currentRouteLocale == null) return null;
      const currentPublishedRouteLocale = demoteRouteLocale(currentRouteLocale);
      const detectedRouteLocale = demoteRouteLocale(routeLocaleByUserLocale[detectedUserLocale]);
      if (detectedRouteLocale === currentPublishedRouteLocale) return null;
      const storedPreference = parseRawCookieValue(rawCookieValue);
      if (
         storedPreference &&
         demoteRouteLocale(routeLocaleByUserLocale[storedPreference]) ===
            currentPublishedRouteLocale
      ) {
         return null;
      }
      const { country: countryCode } = parseUserLocale(detectedUserLocale);
      return {
         userLocale: detectedUserLocale,
         routeLocale: detectedRouteLocale,
         country: countryByCode[countryCode],
      };
   }, [detectedUserLocale, currentRouteLocale, rawCookieValue, demoteRouteLocale]);
}

const LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

function useUserLocaleState({
   currentRouteLocale,
   currentPhpLangId,
   accountLangid,
   geoDetectedUserLocale,
   geoPending = false,
   demoteRouteLocale,
   resolveFromPreference,
}: {
   currentRouteLocale?: LocaleCode;
   currentPhpLangId?: PhpLangid;
   accountLangid: string | null;
   geoDetectedUserLocale?: UserLocaleCode | null;
   geoPending?: boolean;
   demoteRouteLocale: (code: LocaleCode) => LocaleCode;
   resolveFromPreference: boolean;
}) {
   const apiClient = useIFixitApiClient();
   const [rawValue, setCookieValue] = useCookieState(USER_LOCALE_COOKIE, {
      path: '/',
      domain: getCookieDomain(globalThis.location),
      maxAge: LOCALE_COOKIE_MAX_AGE,
      sameSite: 'Lax',
   });
   const [phpViewLocale, setPhpViewLocale] = useCookieState('view-locale', {
      path: '/',
      domain: getCookieDomain(globalThis.location),
      secure: true,
      sameSite: 'None',
   });
   const [phpViewLangid] = useCookieState('view-langid');

   // Carpenter supplies a client-side geo guess (from /api/2.0/geo_locate);
   // Next.js relies on the detected-country cookie set by middleware. Fall back
   // to the middleware cookie when Carpenter's async guess hasn't resolved yet.
   const middlewareDetectedUserLocale = useDetectedUserLocale();
   const geoUserLocale = geoDetectedUserLocale ?? middlewareDetectedUserLocale ?? null;

   const signals = {
      cookieValue: rawValue,
      phpViewLocale,
      phpViewLangid,
      accountLangid,
      geoUserLocale,
   };

   const userLocale = resolveActiveUserLocale();

   function resolveActiveUserLocale(): UserLocaleCode {
      if (currentRouteLocale == null) {
         return resolveUserLocaleForPhpLangId(currentPhpLangId!, signals);
      }
      if (resolveFromPreference) {
         return resolveUserLocaleFromPreference(
            signals,
            defaultUserLocaleByRouteLocale[currentRouteLocale]
         );
      }
      return resolveUserLocaleForRouteLocale(currentRouteLocale, signals, demoteRouteLocale);
   }

   const resolvedRouteLocale = demoteRouteLocale(routeLocaleByUserLocale[userLocale]);
   useEffect(() => {
      // Don't persist the resolved locale until Carpenter geo guess has settled.
      if (geoPending) return;
      if (resolvedRouteLocale !== phpViewLocale) {
         setPhpViewLocale(resolvedRouteLocale);
      }
   }, [geoPending, resolvedRouteLocale, phpViewLocale, setPhpViewLocale]);

   useMigrateUserLocaleCookieToRootDomain();

   const setUserLocale = useCallback(
      (locale: UserLocaleCode) => {
         const { language } = parseUserLocale(locale);
         const routeLocale = demoteRouteLocale(routeLocaleByUserLocale[locale]);
         setCookieValue(locale);
         setPhpViewLocale(routeLocale);
         sendReactNativeMessage({ type: 'locale_change', localeCode: routeLocale });
         return Promise.all([
            setLocalePreference(apiClient, routeLocale),
            setLanguagePreference(apiClient, language),
         ]);
      },
      [apiClient, setCookieValue, setPhpViewLocale, demoteRouteLocale]
   );

   return useMemo(
      () => ({ userLocale, setUserLocale, rawCookieValue: rawValue }),
      [userLocale, setUserLocale, rawValue]
   );
}

const USER_LOCALE_COOKIE = 'user-locale';

/**
 * Migrate the user-locale cookie from the current subdomain (e.g. www.ifixit.com)
 * to the root domain (.ifixit.com) so the preference is shared across subdomains.
 *
 * Runs once on mount. If a subdomain-scoped cookie exists, it is copied to the
 * root domain (unless a root-domain cookie already exists) and then deleted.
 */
function useMigrateUserLocaleCookieToRootDomain() {
   useEffect(() => {
      // No registrable parent domain (localhost, IP hosts) means there is
      // no root domain to migrate to.
      const domain = getCookieDomain(globalThis.location);
      if (!domain) {
         return;
      }
      const valueBefore = readCookie(USER_LOCALE_COOKIE);
      if (valueBefore == null) return;

      // Delete the subdomain-scoped cookie (no domain = current hostname).
      document.cookie = `${USER_LOCALE_COOKIE}=;max-age=0;path=/`;

      const valueAfter = readCookie(USER_LOCALE_COOKIE);
      if (valueAfter == null) {
         // The cookie only existed on the subdomain — rewrite it to the root domain.
         document.cookie = `${USER_LOCALE_COOKIE}=${encodeURIComponent(valueBefore)};path=/;domain=${domain};max-age=${LOCALE_COOKIE_MAX_AGE};SameSite=Lax`;
      }

      notifyCookieChange();
   }, []);
}
